// BETWEEN-bound scan primitives — value-slot plan T16. Pure text scan
// (G11-rule: no tree-sitter); split out of `editorContextSlot.value.ts` to
// keep the recognizer ≤200 lines, mirroring the `editorContextSlot.setScan.ts`
// / `editorContextSlot.valueScan.ts` split.
//
// Ports the BETWEEN arm of the OLD detector's `scanInOrBetweenHead`
// (`intellisense/context/comparisonScanHelpers.ts`) into this layer: a low
// bound is `[NOT] BETWEEN` ending at the boundary; a high bound is `AND`
// ending at the boundary where walking back over EXACTLY ONE simple bound
// token lands on `[NOT] BETWEEN`. That "exactly one simple token" rule is
// what keeps a predicate-conjunction `AND` from being misread as a high
// bound: `x BETWEEN 1 AND 2 AND y = |` — the SECOND `AND`'s one-token walk
// back lands on the FIRST `AND` (not `BETWEEN`), so the match fails and the
// position stays clause-classified, matching the old detector's own
// behavior for that shape.
export type BetweenBoundMatch = {
  readonly bound: 'low' | 'high';
  readonly negated: boolean;
  /** Offset of the operator's first char (of `NOT` when negated) — the LHS
   *  operand ends before this. Mirrors `ComparisonOperatorMatch.opStart`. */
  readonly opStart: number;
};

/** Matches a BETWEEN low bound (`[NOT] BETWEEN` ending at `end`) or high
 *  bound (`AND` ending at `end` that resolves back to `[NOT] BETWEEN`).
 *  `end` is the same whitespace-tolerant boundary the caller already
 *  computed for `matchComparisonOperator` (`editorContextSlot.valueScan.ts`). */
export function matchBetweenBound(
  text: string,
  end: number,
  lowBound: number,
): BetweenBoundMatch | undefined {
  let i = end;
  while (i > lowBound && /\s/.test(text[i - 1]!)) i -= 1;
  if (i <= lowBound) return undefined;
  let w = i;
  while (w > lowBound && /[A-Za-z]/.test(text[w - 1]!)) w -= 1;
  const word = text.slice(w, i).toUpperCase();

  if (word === 'BETWEEN') {
    const notStart = matchOptionalNotBefore(text, w, lowBound);
    return { bound: 'low', negated: notStart !== undefined, opStart: notStart ?? w };
  }

  if (word === 'AND') {
    const boundStart = skipOneBoundTokenBack(text, w, lowBound);
    if (boundStart === undefined) return undefined;
    let k = boundStart;
    while (k > lowBound && /\s/.test(text[k - 1]!)) k -= 1;
    let bw = k;
    while (bw > lowBound && /[A-Za-z]/.test(text[bw - 1]!)) bw -= 1;
    if (text.slice(bw, k).toUpperCase() !== 'BETWEEN') return undefined;
    const notStart = matchOptionalNotBefore(text, bw, lowBound);
    return { bound: 'high', negated: notStart !== undefined, opStart: notStart ?? bw };
  }

  return undefined;
}

/** Returns the start offset of an optional `NOT` ending at `end`, or
 *  `undefined` when none precedes. */
function matchOptionalNotBefore(text: string, end: number, lowBound: number): number | undefined {
  let k = end;
  while (k > lowBound && /\s/.test(text[k - 1]!)) k -= 1;
  let nw = k;
  while (nw > lowBound && /[A-Za-z]/.test(text[nw - 1]!)) nw -= 1;
  return text.slice(nw, k).toUpperCase() === 'NOT' ? nw : undefined;
}

/** Skips exactly ONE simple BETWEEN bound token (number / identifier /
 *  quoted string) ending at `end`; returns the offset just before it, or
 *  `undefined` if the scan runs off the statement start. Mirrors the old
 *  detector's `skipOneBoundToken` (`comparisonScanHelpers.ts`). Multi-word
 *  bounds (`UNBOUNDED PRECEDING`, `1 PRECEDING` — window-frame extents, not
 *  this recognizer's concern) only ever yield their LAST word here, which
 *  then fails the `=== 'BETWEEN'` check above — the frame-extent AND never
 *  resolves as a high bound (see `recognizeValueSlot`'s ROWS/RANGE/GROUPS
 *  frame-start guard for the complementary low-bound collision). */
function skipOneBoundTokenBack(text: string, end: number, lowBound: number): number | undefined {
  let i = end;
  while (i > lowBound && /\s/.test(text[i - 1]!)) i -= 1;
  if (i <= lowBound) return undefined;
  if (text[i - 1] === "'") {
    i -= 1;
    while (i > lowBound && text[i - 1] !== "'") i -= 1;
    if (i <= lowBound) return undefined;
    return i - 1;
  }
  while (i > lowBound && /[A-Za-z0-9_."`[\]$]/.test(text[i - 1]!)) i -= 1;
  return i;
}
